/**
 * Article translation lookup.
 *
 * Structure: ARTICLE_TRANSLATIONS[slug][lang] = { seoTitle, seoDescription,
 * seoKeywords, hero, subhero, body:[{h,p}...], faq:[{q,a}...], machineTranslated:true }
 *
 * The English canonical always comes from articles.js — this file only holds
 * NON-ENGLISH translations. If a translation is missing, articleTranslationsFor()
 * falls back to the English canonical (safe default — nothing renders broken).
 *
 * The per-language files (/app/frontend/src/data/articleTranslations/<lang>.js)
 * are generated by `scripts/translate_articles.py` — an offline batched
 * Claude Sonnet 4.5 script. Only import the languages that have been
 * translated so far (English fallback handles the rest).
 */

import { ARTICLES } from './articles';

// Auto-load every translation file dropped into ./articleTranslations/.
// Uses Webpack's require.context so we don't need to hand-maintain the
// import list every time the offline batch ships a new language.
//
// The batch script (scripts/translate_articles.py) writes files named
// like `ar.js`, `de.js` — each exports a default { [slug]: content } map.
const REGISTRY = {};
try {
  const ctx = require.context('./articleTranslations', false, /\.js$/);
  ctx.keys().forEach((key) => {
    const lang = key.replace('./', '').replace(/\.js$/, '');
    REGISTRY[lang] = ctx(key).default;
  });
} catch (e) {
  // Directory may not exist yet on first run — safe to ignore.
}

/**
 * Return the article body in the requested language, falling back to English
 * if the language is missing or the specific field is missing.
 *
 * Also attaches `machineTranslated: true` when the returned content came from
 * the translation registry (not the canonical) — the UI uses this flag to
 * render the amber "AI-assisted translation" banner.
 */
export const articleTranslationsFor = (article, lang) => {
  if (!article) return null;
  const canonical = {
    hero: article.hero,
    subhero: article.subhero,
    seoTitle: article.seoTitle,
    seoDescription: article.seoDescription,
    seoKeywords: article.seoKeywords,
    body: article.body,
    faq: article.faq,
  };
  if (lang === 'en') return canonical;

  const langBundle = REGISTRY[lang];
  const translated = langBundle && langBundle[article.slug];
  if (!translated) return canonical;

  return {
    hero:            translated.hero || canonical.hero,
    subhero:         translated.subhero || canonical.subhero,
    seoTitle:        translated.seoTitle || canonical.seoTitle,
    seoDescription:  translated.seoDescription || canonical.seoDescription,
    seoKeywords:     translated.seoKeywords || canonical.seoKeywords,
    body:            translated.body || canonical.body,
    faq:             translated.faq || canonical.faq,
    machineTranslated: true,
  };
};

// Utility used by sitemap builder — returns list of translated languages
// available for a given slug (used to emit hreflang alternates).
export const availableTranslationsFor = (slug) => {
  const out = ['en'];
  Object.keys(REGISTRY).forEach((l) => {
    if (REGISTRY[l] && REGISTRY[l][slug]) out.push(l);
  });
  return out;
};

// For diagnostics.
export const REGISTRY_KEYS = Object.keys(REGISTRY);

// Silence unused-import warning if all translation modules are commented out.
void ARTICLES;
